import {Component} from 'react'
import {BUILD_VERSION} from './buildInfo.js'
import {forceRefreshApp} from './forceRefresh.js'
import {getCurrentRoute} from './appRuntime.js'
import {logRuntimeEvent} from './telemetry.js'

const MAX_ERROR_MESSAGE_LENGTH = 240

function describeError(error) {
  const name = typeof error?.name === 'string' && error.name.trim()
    ? error.name.trim()
    : 'Error'
  const message = typeof error?.message === 'string' && error.message.trim()
    ? error.message.trim().slice(0, MAX_ERROR_MESSAGE_LENGTH)
    : 'Unknown frontend error'

  return {name, message}
}

export function isChunkLoadFailure(error) {
  const {name, message} = describeError(error)
  const haystack = `${name} ${message}`.toLowerCase()

  return haystack.includes('chunkloaderror') ||
    haystack.includes('loading chunk') ||
    haystack.includes('failed to fetch dynamically imported module') ||
    haystack.includes('import()') ||
    haystack.includes('dynamically imported module')
}

function buildFailureMetadata(error, reason) {
  const {name, message} = describeError(error)
  return {
    errorName: name,
    errorMessage: message,
    route: getCurrentRoute(),
    buildVersion: BUILD_VERSION,
    recoveryReason: reason,
  }
}

export async function recoverFromFrontendFailure(reason) {
  logRuntimeEvent('frontend_recovery_reload_clicked', {
    route: getCurrentRoute(),
    buildVersion: BUILD_VERSION,
    recoveryReason: reason,
  })
  return forceRefreshApp({reason})
}

export default class AppErrorBoundary extends Component {
  state = {
    error: null,
    isChunkFailure: false,
  }

  static getDerivedStateFromError(error) {
    return {
      error,
      isChunkFailure: isChunkLoadFailure(error),
    }
  }

  componentDidCatch(error, info) {
    const isChunkFailure = isChunkLoadFailure(error)
    const reason = isChunkFailure ? 'frontend-chunk-load-failed' : 'frontend-render-failed'

    logRuntimeEvent(isChunkFailure ? 'frontend_chunk_load_failed' : 'frontend_render_failed', {
      ...buildFailureMetadata(error, reason),
      componentStack: typeof info?.componentStack === 'string'
        ? info.componentStack.slice(0, 500)
        : null,
    })

    if (isChunkFailure) {
      void forceRefreshApp({reason})
    }
  }

  handleReload = () => {
    const reason = this.state.isChunkFailure
      ? 'frontend-chunk-load-recovery-clicked'
      : 'frontend-render-recovery-clicked'
    void recoverFromFrontendFailure(reason)
  }

  render() {
    if (!this.state.error) {
      return this.props.children
    }

    const title = 'Something went wrong'
    const explanation = this.state.isChunkFailure
      ? 'The app could not load the latest screen. Reloading usually restores the current version.'
      : 'The app hit an unexpected screen error. Reload the app to continue.'

    return (
      <main className="app-recovery" role="alert" aria-live="assertive">
        <section className="app-recovery__panel">
          <p className="app-recovery__eyebrow">Finance app</p>
          <h1>{title}</h1>
          <p>{explanation}</p>
          <button type="button" onClick={this.handleReload}>
            Reload app
          </button>
        </section>
      </main>
    )
  }
}
